import { PageHeader, Button, Dialog } from 'Common/Supplyvue-ui'
import { useEffect, useRef, useState } from 'react'
import { Page } from 'Common/Page/Page'
import { Box, CircularProgress, Typography } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import {
  useRunUpdateVolume,
  useSavePlan,
  useUpdateVolume,
  useWeeklyVolume,
  useWorkcentreRunStatus,
} from './PlannedVolume.data'
import { useParams, unstable_usePrompt as usePrompt } from 'react-router-dom'
import {
  EditPlanParamType,
  EditPlannedVolumeTableData,
} from './PlannedVolume.types'
import { Loader } from 'Common/Loader/Loader'
import { toast } from 'react-hot-toast'
import { WORKCENTRE_STATUS } from 'Common/Constants/Constants'
import { useBreadcrumbsData } from 'Common/Breadcrumbs/Breadcrumbs.data'
import { PlannedVolumeDataTable } from './PlannedVolume.MUI.table'
import { skuCodeSort, weekSorter } from 'Common/utils/utils'
import { getApiErrorMessage } from 'Features/auth/axios-client'
import { usePlanBuiltByV2Engine } from 'Common/Charts/StockWaterfall/stockWaterfall.data'

export const PlannedVolumePage = () => {
  const [showDialog, setShowDialog] = useState(false)
  const [data, setData] = useState<EditPlannedVolumeTableData[]>([])
  const theme = useTheme()
  const { sourcesiteId, workcentreId, planId } = useParams<EditPlanParamType>()
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [showSaveConfirmDialog, setShowSaveConfirmDialog] = useState(false)
  const navigate = useNavigate()
  const pendingToSave = useRef({})
  const initialTableRef = useRef<EditPlannedVolumeTableData[]>([])
  const [isSavePending, setIsSavePending] = useState(false)
  const [isAnyWorkcentreRunning, setIsAnyWorkcentreRunning] = useState(false)
  const isAnyWorkcentreRunningRef = useRef(false)

  useBreadcrumbsData({
    plan_id: planId,
    workcentre_id: workcentreId,
  })

  const updateTableData = (
    changedRows: Record<number, EditPlannedVolumeTableData>
  ) => {
    setIsSavePending(true)
    pendingToSave.current = { ...pendingToSave.current, ...changedRows }
  }

  const { data: workcentreRunStatus, isLoading: workCentreRunStatusLoading } =
    useWorkcentreRunStatus({ planId, sourcesiteId, workcentreId })

  useEffect(() => {
    let intervalId: NodeJS.Timeout
    setIsAnyWorkcentreRunning(
      workcentreRunStatus?.is_any_workcentre_being_run ?? false
    )
    if (
      workcentreRunStatus &&
      (workcentreRunStatus?.status === WORKCENTRE_STATUS.INITIATED ||
        workcentreRunStatus?.status === WORKCENTRE_STATUS.RUNNING)
    ) {
      setIsSavePending(false)
      intervalId = setTimeout(() => {
        navigate(`/plan/${planId}`)
      }, 1000)

      return () => {
        clearInterval(intervalId)
      }
    }
  }, [workcentreRunStatus])

  useEffect(() => {
    if (isAnyWorkcentreRunning) {
      isAnyWorkcentreRunningRef.current = true
      toast.error(
        'Another workcentre is being run. Check back later to edit plan for this workcentre.',
        {
          duration: 10000 * 60,
        }
      )
    } else if (
      isAnyWorkcentreRunning === false &&
      isAnyWorkcentreRunningRef.current === true
    ) {
      isAnyWorkcentreRunningRef.current = false
      toast.dismiss()
      toast.success(
        'Other workcentre finished running. Now you can save your changes.'
      )
    }
  }, [isAnyWorkcentreRunning])

  const { data: volumeData, isLoading: isVolumeDataLoading } = useWeeklyVolume(
    {
      sourcesiteId,
      workcentreId,
      planId,
    },
    Boolean(
      workcentreRunStatus && workcentreRunStatus !== WORKCENTRE_STATUS.INITIATED
    )
  )

  useEffect(() => {
    if (volumeData?.weeklyVolumes.length > 0) {
      setData(volumeData.weeklyVolumes)
    }

    if (
      initialTableRef.current.length === 0 &&
      volumeData?.weeklyVolumes.length > 0
    ) {
      initialTableRef.current = volumeData.weeklyVolumes
    }
  }, [volumeData])

  const onSuccess = (savePlan: 'true' | 'false') => {
    runPlan({ planId, sourcesiteId, workcentreId, savePlan })
    initialTableRef.current = [...data]
    toast.success('Volume updated successfully.')
  }

  const onError = () => {
    toast.error('Could not update the value. Please retry.')
  }
  const { mutate: updateVolume } = useUpdateVolume(onSuccess, onError)

  /*********Save plan */
  const onSavePlanSuccess = (message: string, savePlan: 'true' | 'false') => {
    runPlan({ planId, sourcesiteId, workcentreId, savePlan })
    toast.success(message)
  }

  const onSavePlanFail = (message: string) => {
    toast.error(message)
  }
  const { mutate: savePlan, isLoading: isSavePlanLoading } = useSavePlan(
    onSavePlanSuccess,
    onSavePlanFail
  )
  /*******End Save plan */
  const onRunPlanSuccess = () => {
    setShowConfirmDialog(false)
    toast.success('Plan initiated successfully.')
    navigate(`/plan/${planId}`)
  }

  const onRunPlanError = (error: unknown) => {
    setShowConfirmDialog(false)
    toast.error(
      getApiErrorMessage(error, 'Could not update the value. Please retry.')
    )
  }

  const { mutate: runPlan, isLoading: isPlanInitializationRunning } =
    useRunUpdateVolume(onRunPlanSuccess, onRunPlanError)

  // Save plan / Apply changes write the volume, then run the plan; the run is
  // refused on V2 plans, so both stay disabled there (no half-applied edit).
  const { builtByV2Engine } = usePlanBuiltByV2Engine(planId)

  const handleApplyChangesButtonClick = () => {
    setShowConfirmDialog(true)
    setIsSavePending(false)
  }

  const handleConfirmButtonClick = () => {
    // For apply changes
    setShowConfirmDialog(false)
    const dataToServer = Object.values<EditPlannedVolumeTableData>(
      pendingToSave.current
    ).map((updateData: EditPlannedVolumeTableData) => {
      return {
        planId: planId,
        productionDateKey: updateData?.productionDateKey,
        replenishmentDateKey: updateData.dateKey,
        workcentreId: workcentreId,
        sourcesiteId: sourcesiteId,
        skuKey: updateData.skuKey,
        replenishmentSiteKey: updateData.siteKey,
        adjustedPlannedVolume: updateData.adjustedReplenishmentPlanArrival,
      }
    })

    const data = {
      dataToServer,
      savePlan: 'false' as 'true' | 'false',
    }
    updateVolume(data)
  }
  const handleDiscardButtonClick = () => {
    setShowDialog(false)
    navigate(-1)
  }

  const handleSavePlanButtonClick = () => {
    setShowSaveConfirmDialog(true)
    setIsSavePending(false)
  }

  const handleConfirmSaveButtonClick = () => {
    setShowSaveConfirmDialog(false)
    const dataToServer = Object.values<EditPlannedVolumeTableData>(
      pendingToSave.current
    ).map((updateData: EditPlannedVolumeTableData) => {
      return {
        planId: planId,
        productionDateKey: updateData?.productionDateKey,
        replenishmentDateKey: updateData.dateKey,
        workcentreId: workcentreId,
        sourcesiteId: sourcesiteId,
        skuKey: updateData.skuKey,
        replenishmentSiteKey: updateData.siteKey,
        adjustedPlannedVolume: updateData.adjustedReplenishmentPlanArrival,
      }
    })

    const dataToServerToSave = {
      dataToServer,
      savePlan: 'true' as 'true' | 'false',
    }
    savePlan(dataToServerToSave)
    initialTableRef.current = [...data]
  }

  const DialogContent = () => {
    return (
      <Box>
        <Typography sx={{ width: '100%', marginBottom: '20px' }}>
          This will recalculate the constrained plan based on the new volume
          values and may take a while to complete.
        </Typography>
      </Box>
    )
  }

  if (isVolumeDataLoading || workCentreRunStatusLoading) {
    return <Loader />
  }

  const isApplyChangesButtonDisabled = () => {
    return (
      builtByV2Engine || !(isSavePending && isAnyWorkcentreRunning === false)
    )
  }

  // Move usePrompt hook to top level
  usePrompt({
    message: isAnyWorkcentreRunning
      ? 'Updates will be lost if you leave the page. The save button will be activated when processing of other workcentre is complete.'
      : "You haven't updated the plan, Are you sure you want to leave.",
    when: isSavePending,
  })

  return (
    <Page>
      <Box display="flex" flexDirection="column" flex={1}>
        <PageHeader title="Edit planned volume" />
        <>
          <Box>
            <PlannedVolumeDataTable
              data={data ?? []}
              updateTableData={updateTableData}
              weeks={volumeData?.weeks.sort(weekSorter)}
              siteCodes={volumeData?.site_codes ?? []}
              skuCodes={volumeData?.sku_codes?.sort(skuCodeSort) ?? []}
            />
          </Box>

          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              margin: '16px 0px',
            }}
          >
            <Box>
              <Button
                variant="contained"
                color="primary"
                onClick={handleSavePlanButtonClick}
                disabled={isApplyChangesButtonDisabled()}
              >
                {isSavePlanLoading && (
                  <CircularProgress
                    color="inherit"
                    size={16}
                    sx={{ marginRight: '8px' }}
                  />
                )}
                Save plan
              </Button>
            </Box>

            <Box marginLeft={2}>
              <Button
                variant="contained"
                color="primary"
                onClick={handleApplyChangesButtonClick}
                disabled={isApplyChangesButtonDisabled()}
              >
                {isPlanInitializationRunning && (
                  <CircularProgress
                    color="inherit"
                    size={16}
                    sx={{ marginRight: '8px' }}
                  />
                )}
                Apply changes
              </Button>
            </Box>
          </Box>
        </>

        <Dialog
          title="Unsaved changes will be lost"
          content="Are you sure you want to discard the changes ?"
          primaryButtonLabel="Discard"
          secondaryButtonLabel="Cancel"
          open={showDialog}
          handleClose={() => setShowDialog(false)}
          handlePrimaryButtonClick={handleDiscardButtonClick}
          handleSecondaryButtonClick={() => setShowDialog(false)}
        />
        <Dialog
          title="Confirm changes"
          content={<DialogContent />}
          primaryButtonLabel="Confirm"
          secondaryButtonLabel="Cancel"
          open={showConfirmDialog}
          handleClose={() => setShowConfirmDialog(false)}
          handlePrimaryButtonClick={handleConfirmButtonClick}
          handleSecondaryButtonClick={() => setShowConfirmDialog(false)}
        />
        <Dialog
          title="Confirm changes for save"
          content={<DialogContent />}
          primaryButtonLabel="Confirm"
          secondaryButtonLabel="Cancel"
          open={showSaveConfirmDialog}
          handleClose={() => setShowSaveConfirmDialog(false)}
          handlePrimaryButtonClick={handleConfirmSaveButtonClick}
          handleSecondaryButtonClick={() => setShowSaveConfirmDialog(false)}
        />
      </Box>
    </Page>
  )
}
